import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';

export enum BorrowerType {
  LegalEntity = 'legalEntity',
  SoleTrader = 'soleTrader',
}

export enum FinancialStatementForm {
  Large = 'large',
  SmallOrSimplified = 'smallOrSimplified',
}

export type TBorrowerType = TAutocompleteOption | null;

export type TQuarter = '1' | '2' | '3' | '4';

export type TBankIncomeRow = {
  bankId: TAutocompleteOption | null;
  currency: TAutocompleteOption | null;
  total: string;
  salesRevenue: string;
};

export type TBankIncomeCurrencyTotals = {
  currency: TAutocompleteOption;
  total: number;
  salesRevenue: number;
};

export type TBankAverageBalanceRow = {
  bankId: TAutocompleteOption | null;
  currency: TAutocompleteOption | null;
  firstMonthAmount: string;
  secondMonthAmount: string;
  thirdMonthAmount: string;
};

export type TBankAverageBalanceCurrencyTotals = {
  currency: TAutocompleteOption;
  firstMonthAmount: number;
  secondMonthAmount: number;
  thirdMonthAmount: number;
};

export type TCreditOperation = {
  lenderName: TAutocompleteOption | null;
  productType: TAutocompleteOption | null;
  currency: TAutocompleteOption | null;
  limit: string;
  outstandingDebt: string;
  interestRate: string;
  issueDate: string | Date | null;
  maturityDate: string | Date | null;
  overdueDebt: string;
};

export type TCreditOperationCurrencyTotals = {
  currency: TAutocompleteOption;
  limit: number;
  outstandingDebt: number;
  overdueDebt: number;
};

export type TFinancialAssistance = {
  counterpartyName: string;
  currency: TAutocompleteOption | null;
  outstandingDebt: string;
  issueOrReceiptDate: string | Date | null;
  maturityDate: string | Date | null;
  balanceLine: string;
  overdueDebt: string;
};

export type TFinancialAssistanceCurrencyTotals = {
  currency: TAutocompleteOption;
  outstandingDebt: number;
  overdueDebt: number;
};

export type TLoanOrBond = {
  lenderName: TAutocompleteOption | null;
  lenderCode: string;
  receivedDate: string | Date | null;
  maturityDate: string | Date | null;
  currency: TAutocompleteOption | null;
  currentAmount: string;
  balanceLine: string;
  overdueDebt: string;
};

export type TOffBalanceLiability = {
  debtor: string;
  creditor: TAutocompleteOption | null;
  obligationType: string;
  currency: TAutocompleteOption | null;
  limitAmount: string;
  actualAmount: string;
  interestRate: string;
  openDate: string | Date | null;
  endDate: string | Date | null;
};

export type TCurrencyExchange = {
  bank: TAutocompleteOption | null;
  soldCurrency: TAutocompleteOption | null;
  soldAmount: string;
  boughtCurrency: TAutocompleteOption | null;
  boughtAmount: string;
};

export type TCurrencyExchangeCurrencyTotals = {
  currency: TAutocompleteOption;
  soldAmount?: number;
  boughtAmount?: number;
};

export type TLiability = {
  creditor: TAutocompleteOption | null;
  liabilityType: string;
  amount: string;
  purpose: string;
  originDate: string | Date | null;
  repaymentDate: string | Date | null;
  reportLine: string;
};

export type TIncomeBreakdown = {
  counterpartyName: string;
  counterpartyEdrpou: string;
  reportLine: string;
  incomeType: string;
  amount: string;
  sharePercent: string;
};

export type TFinancialInvestment = {
  templateCode?: string;
  name: string;
  edrpou: string;
  sharePercent: string;
  amount: string;
  startDate: string | Date | null;
};

export type TReceivable = {
  templateCode?: string;
  counterpartyName: string;
  edrpou: string;
  subject: string;
  totalDebt: string;
  overdueAmount: string;
  originDate: string | Date | null;
  repaymentDate: string | Date | null;
};

export type TBudgetDebt = {
  debtType: string;
  amount: string;
};

export type TExpense = {
  reportLine: string;
  component: string;
  amount: string;
  sharePercent: string;
};

export type TCourtCase = {
  otherParty: string;
  borrowerName: string;
  borrowerStatus: string;
  subject: string;
  claimAmount: string;
  claimDate: string | Date | null;
  currentState: string;
};

export type TFinancialConditionFormValues = {
  borrowerType: TBorrowerType;
  financialStatementForm: TAutocompleteOption | null;
  borrowerName: string;
  borrowerCode: string;
  reportPeriod: TAutocompleteOption | null;
  reportYear: string;
  reportDate: string | Date | null;
  hasCashFlowTransferRequirement: boolean;
  incomeBanks: TBankIncomeRow[];
  hasAverageBalanceRequirement: boolean;
  averageBalanceBanks: TBankAverageBalanceRow[];
  hasNoCreditOperations: boolean;
  creditOperations: TCreditOperation[];
  providedFinancialAssistance: TFinancialAssistance[];
  receivedFinancialAssistance: TFinancialAssistance[];
  hasNoLoansAndBonds: boolean;
  receivedLoans: TLoanOrBond[];
  bondLoans: TLoanOrBond[];
  offBalanceLiabilities: TOffBalanceLiability[];
  currencyExchanges: TCurrencyExchange[];
  liabilitiesAnnual: TLiability[];
  liabilitiesQuarterly: TLiability[];
  incomeBreakdowns: TIncomeBreakdown[];
  financialInvestments: TFinancialInvestment[];
  receivables: TReceivable[];
  budgetDebts: TBudgetDebt[];
  expensesAnnual: TExpense[];
  expensesQuarterly: TExpense[];
  expensesQuarterlySmall: TExpense[];
  courtCases: TCourtCase[];
  additionalInfo: string;
};

export type TFinancialConditionCertificateDto = {
  isFullFormReporting?: boolean;
  documentDate: string;
  agreementDate?: string;
  borrowerFullName: string;
  borrowerEdrpou: string;
  reportPeriod: string;
  reportDate: string;
  balanceMonth1: string;
  balanceMonth2: string;
  balanceMonth3: string;
  additionalInfo?: string;
  cashInflows: TCashInflowItem[];
  averageBalances: TAverageBalanceItem[];
  creditOperations: TCreditOperationItem[];
  financialAidGiven: TFinancialAidItem[];
  financialAidReceived: TFinancialAidItem[];
  loans: TLoanItem[];
  offBalanceLiabilities: TOffBalanceLiabilityItem[];
  currencyExchanges: TCurrencyExchangeItem[];
  incomeBreakdowns?: TIncomeBreakdownItem[];
  liabilitiesAnnual: TLiabilityItem[];
  liabilitiesQuarterly: TLiabilityItem[];
  financialInvestments: TFinancialInvestmentItem[];
  receivables: TReceivableItem[];
  expensesAnnual: TExpenseItem[];
  expensesQuarterly: TExpenseItem[];
  expensesQuarterlySmall: TExpenseItem[];
  budgetDebts: TBudgetDebtItem[];
  courtCases: TCourtCaseItem[];
};

export type TFinancialConditionCertificateResponse = {
  type: 'FINANCIAL_CONDITION';
  body: TFinancialConditionCertificateDto | null;
  version: number;
  fullName?: string;
  borrowerEdrpou?: string;
  asOfDay?: string;
  childPeriod?: string;
  yearPeriod: string;
  fop: boolean;
};

type TCashInflowItem = {
  bankName?: string;
  bankCode: string | null;
  currency?: string;
  totalAmount?: number;
  revenueAmount?: number;
};

type TAverageBalanceItem = {
  bankName?: string;
  bankCode: string | null;
  currency?: string;
  month1Amount?: number;
  month2Amount?: number;
  month3Amount?: number;
};

type TCreditOperationItem = {
  creditorName?: string;
  productType?: string;
  currency?: string;
  limitAmount?: number;
  debtAmount?: number;
  interestRate?: number;
  issueDate?: string;
  maturityDate?: string;
  overdueAmount?: number;
};

type TFinancialAidItem = {
  counterpartyName?: string;
  currency?: string;
  debtAmount?: number;
  issueDate?: string;
  repaymentDate?: string;
  balanceLine?: string;
  overdueAmount?: number;
};

type TLoanItem = {
  indicator: 'Позики' | 'Облігаційні позики' | 'Позика' | 'Облігаційна позика';
  creditorName?: string;
  creditorEdrpou?: string;
  receiveDate?: string;
  repaymentDate?: string;
  currency?: string;
  currentAmount?: number;
  balanceLine?: string;
  overdueAmount?: number;
};

type TOffBalanceLiabilityItem = {
  debtor?: string;
  creditor?: string;
  obligationType?: string;
  currency?: string;
  limitAmount?: number;
  actualAmount?: number;
  interestRate?: number;
  openDate?: string;
  endDate?: string;
};

type TCurrencyExchangeItem = {
  bankName?: string;
  bankCode: string | null;
  soldCurrency?: string;
  soldAmount?: number;
  boughtCurrency?: string;
  boughtAmount?: number;
};

type TIncomeBreakdownItem = {
  counterpartyName?: string;
  counterpartyEdrpou?: string;
  reportLine?: string;
  component?: string;
  amount?: number;
  sharePercent?: number;
};

type TLiabilityItem = {
  creditor?: string;
  liabilityType?: string;
  amount?: number;
  purpose?: string;
  originDate?: string;
  repaymentDate?: string;
  reportLine?: string;
};

type TFinancialInvestmentItem = {
  name?: string;
  edrpou?: string;
  sharePercent?: number;
  amount?: number;
  startDate?: string;
};

type TReceivableItem = {
  counterpartyName?: string;
  edrpou?: string;
  subject?: string;
  totalDebt?: number;
  overdueAmount?: number;
  originDate?: string;
  repaymentDate?: string;
};

type TExpenseItem = {
  reportLine?: string;
  component?: string;
  amount?: number;
  sharePercent?: number;
};

type TBudgetDebtItem = {
  debtType?: string;
  amount?: number;
};

type TCourtCaseItem = {
  otherParty?: string;
  borrowerName?: string;
  borrowerStatus?: string;
  subject?: string;
  claimAmount?: number;
  claimDate?: string;
  currentState?: string;
};
